import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';

export type CheckoutPlan = 'junior' | 'lead';

async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

// ─── useCheckout ──────────────────────────────────────────────────────────────
// Provides startCheckout() and openPortal() — both redirect to Stripe.
// Currency is determined server-side from the user's IP address.

export function useCheckout() {
  const [isLoading, setIsLoading] = useState(false);
  const [error,     setError]     = useState<string | null>(null);

  // Full-screen overlay while redirecting so cold-start delay doesn't leave
  // the user staring at a frozen button with no feedback.
  useEffect(() => {
    if (!isLoading) return;
    const overlay = document.createElement('div');
    overlay.id = 'checkout-overlay';
    overlay.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:9999',
      'display:flex', 'flex-direction:column', 'align-items:center', 'justify-content:center',
      'background:rgba(227,232,241,0.92)', 'backdrop-filter:blur(8px)', 'gap:16px',
    ].join(';');
    overlay.innerHTML = `
      <div style="width:40px;height:40px;border-radius:50%;border:3px solid rgba(74,108,247,0.2);border-top-color:#4A6CF7;animation:spin 0.8s linear infinite"></div>
      <div style="text-align:center">
        <p style="font-size:11px;font-weight:900;letter-spacing:0.3em;text-transform:uppercase;color:#1A1F36">Redirecting to Stripe</p>
        <p style="font-size:10px;font-weight:700;color:rgba(26,31,54,0.4);margin-top:4px">Secure checkout</p>
      </div>
      <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
    `;
    document.body.appendChild(overlay);
    return () => { document.getElementById('checkout-overlay')?.remove(); };
  }, [isLoading]);

  async function startCheckout(plan: CheckoutPlan) {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');

      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ plan }),
      });

      const text = await res.text();
      let data: any = {};
      try { data = JSON.parse(text); } catch {}
      if (!res.ok) throw new Error(data.error ?? text.slice(0, 200) ?? 'Checkout failed');
      if (!data.url) throw new Error('No checkout URL returned');

      window.location.href = data.url;
    } catch (err: any) {
      console.error('[useCheckout] startCheckout error:', err);
      Sentry.captureException(err, { tags: { hook: 'useCheckout', action: 'startCheckout' }, extra: { plan } });
      setError(err.message ?? 'Something went wrong');
      setIsLoading(false);
    }
    // Don't setIsLoading(false) on success — page is redirecting
  }

  async function openPortal() {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');

      const res = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      const text = await res.text();
      let data: any = {};
      try { data = JSON.parse(text); } catch {}
      if (!res.ok) throw new Error(data.error ?? text.slice(0, 200) ?? 'Portal failed');
      if (!data.url) throw new Error('No portal URL returned');

      window.location.href = data.url;
    } catch (err: any) {
      console.error('[useCheckout] openPortal error:', err);
      Sentry.captureException(err, { tags: { hook: 'useCheckout', action: 'openPortal' } });
      setError(err.message ?? 'Something went wrong');
      setIsLoading(false);
    }
  }

  return { startCheckout, openPortal, isLoading, error };
}
